import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { Field, FieldGrid, SectionCard } from '../../../components/Section';
import { treasuryPersonalEmailFormFields } from '../../../forms/fields/formFields/treasuryPersonalEmail';
import { treasuryPersonalEmailSchema } from '../../../forms/validationSchema/formSchema/treasuryPersonalEmail';
import { usePermissions } from '../../../hooks';
import { useUpdateTreasuryPersonalEmailMutation } from '../../../redux/features/treasury/treasury.apiSlice';
import { TreasuryCompanySetup } from '../../../types/treasury';
import * as permissions from '../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../utils/treasury';

type TreasuryPersonalEmailStepProps = {
  setup: TreasuryCompanySetup;
  onDone: () => void;
};

/**
 * Step 3 — the owner's personal email (what a ratbi+ employee sets in the app, the admin sets here). madkhol-user only
 * replaces the email while it still equals the work email; after that the call changes nothing, so saving twice is
 * harmless.
 */
export const TreasuryPersonalEmailStep = ({ setup, onDone }: TreasuryPersonalEmailStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canUpdate = usePermissions({ items: [permissions.Treasury.updateEmployee] });
  const [updateTreasuryPersonalEmail, { isLoading: isSaving }] = useUpdateTreasuryPersonalEmailMutation();

  const { handleSubmit, values, handleChange, touched, errors, isValid } = useFormik({
    initialValues: {
      [treasuryPersonalEmailFormFields.personalEmail]: '',
    },
    validationSchema: treasuryPersonalEmailSchema,
    validateOnMount: true,
    onSubmit: async (formValues) => {
      if (!setup.employeeId) return;

      try {
        const response = await updateTreasuryPersonalEmail({
          companyId: setup.companyId,
          employeeId: setup.employeeId,
          payload: { personalEmail: String(formValues[treasuryPersonalEmailFormFields.personalEmail]).trim() },
        }).unwrap();

        // madkhol-user answers 200 either way; only needsUserUpdate says the account email really changed.
        if (response?.needsUserUpdate) {
          enqueueSnackbar('Personal email saved!', { variant: 'success' });
        } else {
          enqueueSnackbar('No change — the account already uses a personal email, not the work email.', {
            variant: 'warning',
          });
        }
        onDone();
      } catch (error) {
        enqueueSnackbar(getTreasuryErrorMessage(error, 'Personal email update error!'), { variant: 'error' });
      }
    },
  });

  return (
    <SectionCard
      title="Personal Email"
      description="Replaces the account email only while the account still uses the work email."
    >
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <FieldGrid columns={1}>
              <Field label="Work Email (now)" value={setup.employee?.email} />
            </FieldGrid>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              size="small"
              id={treasuryPersonalEmailFormFields.personalEmail}
              name={treasuryPersonalEmailFormFields.personalEmail}
              label="Personal Email"
              type="email"
              value={values[treasuryPersonalEmailFormFields.personalEmail]}
              onChange={handleChange}
              error={touched.personalEmail && !!errors.personalEmail}
              helperText={touched.personalEmail && errors.personalEmail}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={isSaving}
                startIcon={<SaveIcon />}
                disabled={!isValid || isSaving || !canUpdate}
              >
                Save Personal Email
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </form>
    </SectionCard>
  );
};
